import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateControlGroupMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

type UpdateControlGroupInput = {
  Id: string;
  Title: string;
  Description: string;
  Owner: string;
  OriginalTimestamp: string;
  CustomAttributeData?: CustomAttributeData | null;
};

function mapTrpcResponseToGraphQL(): UpdateControlGroupMutation {
  return {
    update_control_group: {
      __typename: 'control_group_mutation_response',
      // trpc throws an error if no rows are affected (NotFound/Conflict)
      affected_rows: 1,
    },
  };
}

export const useUpdateControlGroupTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.controlGroup.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.controlGroup.controlGroupById.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.controlGroup.controlGroups.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.controlGroup.controlGroupsByTitle.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.controlGroup.register.queryKey(),
        });
      },
    }),
  });

  return {
    updateControlGroup: async (
      variables: UpdateControlGroupInput
    ): Promise<UpdateControlGroupMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
